import { apiClient } from '@/core/api/client';
import type {
  PlansResponse,
  PlanResponse,
  StatsResponse,
  StatusResponse,
  NotificationsResponse,
  WakeupsResponse,
  PendingCountResponse,
  FeedbackSummaryResponse,
  FeedbackHistoryResponse,
  ExecutionLogsResponse,
  DailyBriefingResponse,
  DailyAutomationsResponse,
  TriageHistoryResponse,
  ActivityResponse,
  StepReviewStatus,
  StepContent,
  RefusalReason,
  RegistryResponse,
  SituationRegistry,
  DecisionsResponse,
} from '../types/agent.types';

const BASE = '/api/admin/agent';

export const agentAPI = {
  // ============================================================================
  // Plans
  // ============================================================================

  async getPlans(params?: {
    role?: string;
    status?: string;
    priority?: string;
    plan_type?: string;
    search?: string;
    view?: string;
  }): Promise<PlansResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
    }
    const qs = searchParams.toString();
    return apiClient.get<PlansResponse>(`${BASE}/plans${qs ? `?${qs}` : ''}`);
  },

  async getPlan(id: string): Promise<PlanResponse> {
    return apiClient.get<PlanResponse>(`${BASE}/plans/${id}`);
  },

  async reviewStep(
    planId: string,
    stepNumber: number,
    data: {
      review_status: StepReviewStatus;
      modified_content?: StepContent;
      refusal_reason?: RefusalReason;
    }
  ): Promise<PlanResponse> {
    return apiClient.patch<PlanResponse>(`${BASE}/plans/${planId}/steps/${stepNumber}`, data);
  },

  async validatePlan(id: string, comment?: string): Promise<PlanResponse> {
    return apiClient.post<PlanResponse>(`${BASE}/plans/${id}/validate`, { comment });
  },

  async retryPlan(id: string): Promise<PlanResponse> {
    return apiClient.post<PlanResponse>(`${BASE}/plans/${id}/retry`);
  },

  // ============================================================================
  // Stats & Status
  // ============================================================================

  async getStats(): Promise<StatsResponse> {
    return apiClient.get<StatsResponse>(`${BASE}/stats`);
  },

  async getStatus(): Promise<StatusResponse> {
    return apiClient.get<StatusResponse>(`${BASE}/status`);
  },

  async getPendingCount(): Promise<PendingCountResponse> {
    return apiClient.get<PendingCountResponse>(`${BASE}/pending-count`);
  },

  // ============================================================================
  // Notifications
  // ============================================================================

  async getNotifications(): Promise<NotificationsResponse> {
    return apiClient.get<NotificationsResponse>(`${BASE}/notifications`);
  },

  async markNotificationRead(id: string): Promise<{ success: boolean }> {
    return apiClient.patch<{ success: boolean }>(`${BASE}/notifications/${id}/read`);
  },

  async markAllNotificationsRead(): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(`${BASE}/notifications/read-all`);
  },

  // ============================================================================
  // History
  // ============================================================================

  async getHistory(params?: {
    plan_type?: string;
    priority?: string;
    search?: string;
    page?: number;
  }): Promise<PlansResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.set(key, String(value));
      });
    }
    const qs = searchParams.toString();
    return apiClient.get<PlansResponse>(`${BASE}/history${qs ? `?${qs}` : ''}`);
  },

  // ============================================================================
  // Scheduled wake-ups
  // ============================================================================

  async getScheduledWakeups(): Promise<WakeupsResponse> {
    return apiClient.get<WakeupsResponse>(`${BASE}/scheduled-wakeups`);
  },

  async forceWake(affaireId: number): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(`${BASE}/sleep/${affaireId}/force-wake`);
  },

  // ============================================================================
  // Feedback learning
  // ============================================================================

  async getFeedbackSummary(): Promise<FeedbackSummaryResponse> {
    return apiClient.get<FeedbackSummaryResponse>(`${BASE}/feedback/summary`);
  },

  async triggerLearning(): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(`${BASE}/trigger-learning`);
  },

  // ============================================================================
  // Feedback history (learning view)
  // ============================================================================

  async getFeedbackHistory(params?: { limit?: number; page?: number }): Promise<FeedbackHistoryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.page) searchParams.set('page', String(params.page));
    const qs = searchParams.toString();
    return apiClient.get<FeedbackHistoryResponse>(`${BASE}/feedback/history${qs ? `?${qs}` : ''}`);
  },

  // ============================================================================
  // Execution logs (journal view)
  // ============================================================================

  async getExecutionLogs(params?: { limit?: number; page?: number; action_type?: string; status?: string }): Promise<ExecutionLogsResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.set(key, String(value));
      });
    }
    const qs = searchParams.toString();
    return apiClient.get<ExecutionLogsResponse>(`${BASE}/execution-logs${qs ? `?${qs}` : ''}`);
  },

  // ============================================================================
  // Daily Briefing
  // ============================================================================

  async getDailyBriefing(): Promise<DailyBriefingResponse> {
    return apiClient.get<DailyBriefingResponse>(`${BASE}/daily-briefing`);
  },

  async triggerBriefing(): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(`${BASE}/trigger-briefing`);
  },

  // ============================================================================
  // Daily Automations
  // ============================================================================

  async getDailyAutomations(): Promise<DailyAutomationsResponse> {
    return apiClient.get<DailyAutomationsResponse>(`${BASE}/daily-automations`);
  },

  // ============================================================================
  // Email Triage History
  // ============================================================================

  async getTriageHistory(params?: { limit?: number }): Promise<TriageHistoryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiClient.get<TriageHistoryResponse>(`/api/claude-agent/email-triage/history${qs ? `?${qs}` : ''}`);
  },

  // ============================================================================
  // Activity (analysis log)
  // ============================================================================

  async getActivity(params?: { limit?: number; offset?: number }): Promise<ActivityResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return apiClient.get<ActivityResponse>(`${BASE}/activity${qs ? `?${qs}` : ''}`);
  },

  // ============================================================================
  // V2 Registry
  // ============================================================================

  async getRegistry(): Promise<RegistryResponse> {
    return apiClient.get<RegistryResponse>(`${BASE}/v2/registry`);
  },

  async updateSituation(id: string, data: Partial<SituationRegistry> & { mode_change_reason?: string }): Promise<any> {
    return apiClient.patch(`${BASE}/v2/registry/${id}`, data);
  },

  async togglePause(): Promise<any> {
    return apiClient.post(`${BASE}/v2/toggle-pause`);
  },

  async killAll(): Promise<any> {
    return apiClient.post(`${BASE}/v2/kill`);
  },

  async cancelSituationPlans(situationType: string): Promise<any> {
    return apiClient.post(`${BASE}/v2/cancel-situation-plans`, {
      situation_type: situationType,
    });
  },

  async forceCooldown(affaireId: number, days: number, situationType?: string): Promise<any> {
    return apiClient.post(`${BASE}/v2/force-cooldown`, {
      affaire_id: affaireId,
      days,
      situation_type: situationType,
    });
  },

  async clearCooldown(affaireId: number): Promise<any> {
    return apiClient.post(`${BASE}/v2/clear-cooldown`, {
      affaire_id: affaireId,
    });
  },

  // ============================================================================
  // V2 Decisions
  // ============================================================================

  async getDecisions(params?: {
    affaire_id?: number;
    situation?: string;
    outcome?: string;
    limit?: number;
  }): Promise<DecisionsResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.set(key, String(value));
      });
    }
    const qs = searchParams.toString();
    const raw = await apiClient.get<any>(`${BASE}/v2/decisions${qs ? `?${qs}` : ''}`);
    // Map DB column names to frontend type names
    return {
      success: raw.success,
      data: (raw.data || []).map((d: any) => ({
        id: d.id,
        affaire_id: d.affaire_id,
        situation_type: d.situation_selected || d.situation_type || '',
        mode: d.situation_mode || d.mode || '',
        outcome: d.outcome || '',
        confidence: d.claude_confidence ?? d.confidence ?? null,
        reasoning: d.claude_reasoning || d.reasoning || '',
        gate_results: d.gate_results,
        input_snapshot: d.input_snapshot,
        full_prompt: d.full_prompt,
        outcome_detail: d.outcome_detail,
        cost_usd: d.cost_usd,
        latency_ms: d.latency_ms,
        plan_id: d.plan_id,
        created_at: d.created_at,
      })),
    } as DecisionsResponse;
  },
};
